/**
 * Prüfpflicht in Schritt 1: Ohne aktuelle KI-Prüfung der Anzeige geht es nicht weiter;
 * bei mindestens 1 nicht erfüllten Punkt kommt vorher eine Warnung.
 * „Weiter“ und die Stepper-Sprünge von Schritt 1 nach vorn nutzen dieselbe Entscheidung.
 */

import { AdCheckItem, AdCheckResult } from '../../campaign-wizard.types';

export type StoredAdCheckLike =
  | (AdCheckResult & { checkedText: string; checkedHash?: string })
  | null;

/** Ab so vielen nicht erfüllten Punkten wird gewarnt. */
export const GATE_RED_THRESHOLD = 1;

/** Text ohne Rauschen: Zeilenenden, mehrfache Leerzeichen, Ränder. */
export const normalizeAdText = (text: string): string => text.replace(/\s+/g, ' ').trim();

/** Stabile Signatur (FNV-1a, 32 bit + Länge) des normalisierten Textes. */
export const hashAdText = (text: string): string => {
  const normalized = normalizeAdText(text);
  let h = 0x811c9dc5;
  for (let i = 0; i < normalized.length; i += 1) {
    h ^= normalized.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${h.toString(16).padStart(8, '0')}-${normalized.length}`;
};

/** Alles, was zur Anzeige gehört (Anzeige + Zusatzfelder von Schritt 1). Ältere Entwürfe haben nur `briefing`. */
export interface AdParts {
  briefing: string;
  briefingExtras?: string;
}

/**
 * Text, über den Prüfung und Hash laufen. Ohne Ergänzungen ist er GENAU der Anzeigentext
 * (Hashes älterer Entwürfe bleiben gültig). Muss identisch zu `adSignatureSource` im Backend (`ad-check-hash.ts`) sein.
 */
export const adSignatureSource = (parts: AdParts): string => {
  const extras = (parts.briefingExtras ?? '').trim();
  if (!extras) return parts.briefing;
  return `${parts.briefing}\n\n[[ergaenzungen]]\n${extras}`;
};

/** Der Anzeigentext allein oder die ganze Anzeige mit Zusatzfeldern (z. B. der Wizard-Zustand). */
export type AdSource = string | AdParts;

const sourceText = (source: AdSource): string =>
  typeof source === 'string' ? source : adSignatureSource(source);

/** Signatur des geprüften Textes; ältere Entwürfe ohne `checkedHash` rechnen sie aus `checkedText`. */
const checkedSignature = (adCheck: NonNullable<StoredAdCheckLike>): string =>
  adCheck.checkedHash || hashAdText(adCheck.checkedText);

export type GateStatus = 'unchecked' | 'stale' | 'checked';

/** Ist die Anzeige im aktuellen Stand geprüft? */
export const gateStatus = (adCheck: StoredAdCheckLike, briefing: AdSource): GateStatus => {
  if (!adCheck) return 'unchecked';
  return checkedSignature(adCheck) === hashAdText(sourceText(briefing)) ? 'checked' : 'stale';
};

/** Name des Prüfpunkts „Ort“ (Pflichtangabe: konkreter Ort oder ausdrücklich 100 % remote). */
export const LOCATION_LABEL = 'Ort';

/** Fehlt in der Prüfung der Ort? Nur der neue Punkt „Ort“ zählt; Altentwürfe und Ersatzergebnisse ohne Punkte nicht. */
export const locationMissing = (adCheck: StoredAdCheckLike): boolean =>
  !!adCheck && adCheck.items.some(i => i.label === LOCATION_LABEL && !i.ok);

/** Steht die Ortsfrage (Pflicht) gerade im Prüfergebnis? Nur bei aktueller Prüfung mit fehlendem Ort – sonst ist der Fußzeilen-Hinweis veraltet. */
export const locationQuestionVisible = (adCheck: StoredAdCheckLike, source: AdSource): boolean =>
  gateStatus(adCheck, source) === 'checked' && locationMissing(adCheck);

/** Fester Fehlercode des Servers, wenn der Ort der Stelle fehlt. */
export const LOCATION_REQUIRED_CODE = 'LOCATION_REQUIRED';

export const isLocationRequiredError = (err: { status?: number; code?: string }): boolean =>
  err.code === LOCATION_REQUIRED_CODE;

/** Nicht erfüllte Punkte des Prüfergebnisses. */
export const redPoints = (adCheck: StoredAdCheckLike): AdCheckItem[] =>
  adCheck ? adCheck.items.filter(i => !i.ok) : [];

/** Platzhalter in eckigen Klammern wie „[Spanne eintragen]“; Geschlechtszusätze „[m/w/d]“ zählen nicht. */
export const PLACEHOLDER = /\[(?![mwfdx](?:\s*\/\s*[mwfdx])+\])[^\]\n]*\]/i;

/** Stehen in den Ergänzungen noch unausgefüllte „[…]“-Platzhalter? */
export const hasPlaceholders = (extras: string): boolean => PLACEHOLDER.test(extras);

/** Verbliebene Platzhalter in den Ergänzungen der Anzeige (nur bei Quellen mit Zusatzfeldern). */
export const placeholdersIn = (source: AdSource): boolean =>
  typeof source === 'string' ? false : hasPlaceholders(source.briefingExtras ?? '');

export type GateDecision = 'check' | 'location' | 'warn' | 'pass';

/**
 * Was passiert beim Weiterschalten aus Schritt 1?
 * - `check`: noch nicht (oder nicht mehr) geprüft → Prüfung starten, nicht weiterschalten
 * - `location`: geprüft, aber der Ort fehlt (Pflichtangabe, nicht überspringbar) → Ortsfrage zeigen
 * - `warn`: geprüft, aber ≥ 1 Punkt nicht erfüllt oder „[…]“-Platzhalter in den Ergänzungen → Popup
 * - `pass`: geprüft, kein Punkt offen → weiter
 * Das Ergebnis der Heuristik (`source: 'heuristic'`) zählt wie das der KI.
 */
export const gateDecision = (adCheck: StoredAdCheckLike, briefing: AdSource): GateDecision => {
  if (gateStatus(adCheck, briefing) !== 'checked') return 'check';
  if (locationMissing(adCheck)) return 'location';
  // Auch ein verbliebener „[…]“-Platzhalter in den Ergänzungen löst den Hinweis aus (weiter geht es trotzdem).
  return redPoints(adCheck).length >= GATE_RED_THRESHOLD || placeholdersIn(briefing) ? 'warn' : 'pass';
};

/** Gespeichertes Prüfergebnis mit Signatur des geprüften Textes. */
export const withSignature = (result: AdCheckResult, source: AdSource) => {
  const text = sourceText(source);
  return { ...result, checkedText: text, checkedHash: hashAdText(text) };
};

/** Ersatzergebnis, wenn die Prüfung wiederholt nicht erreichbar war: keine Lücken, Quelle Heuristik. */
export const emptyCheckResult = (): AdCheckResult => ({
  level: 0,
  score: 0,
  total: 6,
  summary: '',
  items: [],
  source: 'heuristic',
});

/** Fester Fehlercode des Servers, wenn die Anzeigenprüfung fehlt oder veraltet ist. */
export const AD_CHECK_REQUIRED_CODE = 'AD_CHECK_REQUIRED';

/** Erkennt die Ablehnung des Servers (422) am festen Code – nicht am Wortlaut der Meldung. */
export const isCheckRequiredError = (err: { status?: number; code?: string }): boolean =>
  err.code === AD_CHECK_REQUIRED_CODE;

/** Ist die Anzeige im aktuellen Stand geprüft (Hash passt zum Briefing)? */
export const adCheckValid = (adCheck: StoredAdCheckLike, briefing: AdSource): boolean =>
  gateStatus(adCheck, briefing) === 'checked';

/**
 * Welcher Schritt darf geöffnet werden? Ein Schritt ≥ 2 (z. B. per `?step=N`, Browser-Vorwärts,
 * geladener Entwurf oder Klon) nur mit gültiger Prüfung; sonst Schritt 1.
 */
export const allowedStep = (
  requested: number,
  adCheck: StoredAdCheckLike,
  briefing: AdSource
): number => (requested >= 2 && !adCheckValid(adCheck, briefing) ? 1 : requested);

/** Darf der Nutzer „Trotzdem weiter“ ohne Prüfergebnis wählen? Erst nach dem zweiten Fehlschlag. */
export const canSkipCheck = (failures: number): boolean => failures >= 2;
